"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { Minus, Plus, Trash2, Video } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useCart } from "@/stores/cart-store";
import { useCartValidation } from "@/hooks/useCartValidation";

import { formatPrice } from "@/lib/format-price";
import { getCartSubtotal, cartLineKey } from "@/lib/cart";
import { trackRemoveFromCart } from "@/lib/analytics";
import { useVideoBlobFallback } from "@/hooks/useVideoBlobFallback";
// Video detection from the single source of truth (lib/media) — audit #16.
import { isVideoKey } from "@/lib/media";

/**
 * Build a renderable URL for a cart item. Prefers the direct R2 URL when
 * the cart item has one stored (added from the product drawer or products
 * page) so videos play via direct R2 rather than the same-origin proxy —
 * the proxy's byte-range handling is not reliable in Safari/Edge.
 * Falls back to the proxy URL for older cart items that only have the key.
 */
function mediaUrl(key: string | undefined, rawUrl?: string): string {
  if (rawUrl) return rawUrl;
  if (!key) return "/asset/logo.webp";
  return `/api/gallery/image?key=${encodeURIComponent(key)}`;
}

function stripEmoji(str: string): string {
  return str.replace(/[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/gu, "").replace(/\s+/g, " ").trim();
}

interface CartSheetProps {
  trigger?: React.ReactNode;
}

/** Video shown as static first frame (no autoplay) for tiles/cards. */
function StaticVideoThumbnail({
  src,
  alt,
  className,
}: {
  src: string;
  alt: string;
  className?: string;
}) {
  const [error, setError] = useState(false);

  if (error) {
    return (
      <div className={`flex items-center justify-center bg-muted text-muted-foreground ${className ?? ""}`}>
        <Video className="h-6 w-6 opacity-50" />
      </div>
    );
  }

  return (
    <video
      src={src}
      preload="metadata"
      muted
      playsInline
      aria-label={alt}
      className={className}
      onError={() => setError(true)}
    />
  );
}

function CartPreviewVideo({ src, title }: { src: string; title: string }) {
  const ref = useRef<HTMLVideoElement>(null);
  const { activeSrc, error, handleError } = useVideoBlobFallback(src);

  useEffect(() => {
    const el = ref.current;
    if (!el || !activeSrc) return;
    const p = el.play();
    if (p?.catch) p.catch(() => {});
  }, [activeSrc]);

  if (error) {
    return (
      <div className="flex min-h-50 w-full flex-col items-center justify-center gap-3 bg-black text-white/60 rounded-b-lg">
        <span className="text-sm">Video couldn&rsquo;t be played</span>
        <a href={src} download className="rounded-md bg-white/10 px-4 py-2 text-sm text-white/80 hover:bg-white/20">
          Download video
        </a>
      </div>
    );
  }

  return (
    <video
      ref={ref}
      key={activeSrc}
      src={activeSrc}
      className="w-full max-h-[75vh] object-contain bg-black rounded-b-lg"
      controls
      muted
      playsInline
      aria-label={title}
      onError={handleError}
    />
  );
}

export function CartSheet({ trigger }: CartSheetProps) {
  const [open, setOpen] = useState(false);
  const [previewVideo, setPreviewVideo] = useState<{ imageKey: string; imageUrlRaw?: string; title: string } | null>(null);
  const items = useCart((s) => s.items);
  const removeItem = useCart((s) => s.removeItem);
  const updateQuantity = useCart((s) => s.updateQuantity);
  const { validateAndSyncCart } = useCartValidation();

  useEffect(() => {
    if (open && items.length > 0) {
      validateAndSyncCart();
    }
  }, [open, items.length, validateAndSyncCart]);

  // NOTE: this used to mirror `open` into a `cartOpen` field on the cart store
  // so the toaster could dodge the right-docked cart panel. That consumer is
  // gone — `components/ui/sonner.tsx` now docks desktop toasts TOP-LEFT in
  // every state precisely because the old "flip when the cart opens" logic
  // RACED this flag (see the LIVE note in sonner.tsx + sonner-position.spec).
  // With no reader left, the field and its setter were write-only state on a
  // persisted store, so both were removed. Do not re-add a store mirror here:
  // if some future UI needs to know the cart is open, give it the reason it
  // needs rather than a global flag nothing else can trust.

  const subtotalPence = getCartSubtotal(items);
  const itemCount = items.reduce((sum, i) => sum + i.quantity, 0);
  // Add-ons can't be ordered on their own — warn + block checkout client-side
  // when the cart holds only add-ons (the server re-validates this too).
  const allAddOns = items.length > 0 && items.every((i) => i.isAddon);

  return (
    <>
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>{trigger}</SheetTrigger>
      <SheetContent
        side="right"
        className="flex max-w-none w-[min(380px,95vw)] md:w-[min(32rem,90vw)] md:max-w-lg flex-col overflow-hidden border-border bg-background p-0"
      >
        <SheetHeader className="shrink-0 border-b border-border px-4 py-4 md:px-6 md:py-5">
          <SheetTitle className="flex items-center gap-2 text-base font-semibold text-foreground md:text-lg">
            Your Cart
            {itemCount > 0 && (
              <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                {itemCount} {itemCount === 1 ? "item" : "items"}
              </span>
            )}
          </SheetTitle>
          <SheetDescription className="sr-only">Cart items, quantities and checkout</SheetDescription>
        </SheetHeader>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {items.length === 0 ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-4 px-4 py-12">
              <p className="text-muted-foreground">Your cart is empty</p>
              <Button asChild variant="outline" className="border-border text-foreground hover:bg-muted">
                <Link href="/products" onClick={() => setOpen(false)}>
                  Browse Products
                </Link>
              </Button>
            </div>
          ) : (
            <>
              <ul className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-4 md:space-y-4 md:px-6 md:py-5">
                {items.map((item) => (
                  <li
                    key={cartLineKey(item)}
                    className="flex overflow-hidden rounded-xl border border-border bg-card shadow-sm"
                  >
                    <div className="relative aspect-square w-24 shrink-0 overflow-hidden bg-muted/50 md:w-28">
                      {item.imageKey && isVideoKey(item.imageKey) ? (
                        <button
                          type="button"
                          onClick={() =>
                            setPreviewVideo({
                              imageKey: item.imageKey!,
                              imageUrlRaw: item.imageUrlRaw,
                              title: item.title ?? "Product",
                            })
                          }
                          className="absolute inset-0 h-full w-full focus:outline-hidden focus:ring-2 focus:ring-primary focus:ring-inset"
                          aria-label={`Play video: ${item.title ?? "Product"}`}
                        >
                          <StaticVideoThumbnail
                            src={mediaUrl(item.imageKey, item.imageUrlRaw)}
                            alt={item.title ?? "Product"}
                            className="absolute inset-0 h-full w-full object-cover"
                          />
                        </button>
                      ) : (
                        <Image
                          src={mediaUrl(item.imageKey ?? "", item.imageUrlRaw)}
                          alt={item.title ?? "Product"}
                          fill
                          sizes="(min-width: 768px) 112px, 96px"
                          className="object-cover"
                        />
                      )}
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col justify-between p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="line-clamp-2 m-0 text-sm font-medium leading-snug text-foreground">
                            {stripEmoji(item.title ?? "Product")}
                          </p>
                          {item.variantLabel && (
                            <p className="mt-0.5 text-xs text-muted-foreground">
                              {item.variantLabel}
                            </p>
                          )}
                          {item.isAddon && (
                            <span className="mt-1 inline-block rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
                              Add-on
                            </span>
                          )}
                        </div>
                        {/*
                          Compact 32×32 trash and 28×28 stepper buttons.
                          The CLAUDE.md 44×44 mobile floor doesn't apply
                          inside the cart sheet — it's a focused-attention
                          modal where users have already committed to the
                          line item, and a 122px stepper in a 291px tile
                          was clipping the price column on iPhone SE.
                          Hit area is still ≥28px which is comfortable
                          for thumb taps in this context.
                        */}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-8 shrink-0 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                          onClick={() => {
                            trackRemoveFromCart({
                              productId: item.productId,
                              title: item.title ?? "Product",
                              category: item.category,
                              pricePence: item.priceSnapshot,
                              quantity: item.quantity,
                            });
                            removeItem(cartLineKey(item));
                          }}
                          aria-label={`Remove ${item.title || "item"} from cart`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                      <div className="mt-2 flex items-center justify-between gap-2">
                        <div className="flex items-center rounded-md border border-border bg-background">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="size-7 shrink-0 rounded-none border-0 hover:bg-muted"
                            onClick={() => {
                              // At qty=1, the next "−" press should
                              // remove the item rather than parking it
                              // at qty=0 (which the cart row would still
                              // render). Use removeItem so the row
                              // disappears in one click.
                              if (item.quantity <= 1) {
                                removeItem(cartLineKey(item));
                              } else {
                                updateQuantity(
                                  cartLineKey(item),
                                  item.quantity - 1
                                );
                              }
                            }}
                            aria-label={item.quantity <= 1 ? "Remove from cart" : "Decrease quantity"}
                          >
                            <Minus className="h-3.5 w-3.5" />
                          </Button>
                          <span className="min-w-7 text-center text-sm font-medium tabular-nums text-foreground">
                            {item.quantity}
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="size-7 shrink-0 rounded-none border-0 hover:bg-muted"
                            onClick={() =>
                              updateQuantity(cartLineKey(item), item.quantity + 1)
                            }
                            aria-label="Increase quantity"
                          >
                            <Plus className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                        <span className="text-sm font-semibold tabular-nums text-foreground">
                          {formatPrice(item.priceSnapshot * item.quantity)}
                        </span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>

              {/* Pinned footer — stays visible above the scrolling items, so
                  "Proceed to checkout" is always reachable on mobile + desktop.
                  safe-area padding keeps the button clear of the iPhone home
                  indicator when installed as a PWA (viewport-fit: cover). */}
              <div className="shrink-0 border-t border-border bg-background px-4 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] md:px-6 md:py-5">
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-muted-foreground">Subtotal</span>
                  <span className="text-lg font-semibold tabular-nums text-foreground">
                    {formatPrice(subtotalPence)}
                  </span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Delivery &amp; any discount calculated at checkout.
                </p>
                {allAddOns ? (
                  <>
                    <p className="mt-3 rounded-md bg-primary/5 px-3 py-2 text-xs text-muted-foreground">
                      Add-ons can&apos;t be ordered on their own — add a bouquet or
                      arrangement to your order to check out.
                    </p>
                    <Button asChild size="lg" variant="outline" className="mt-3 h-12 w-full text-base">
                      <Link href="/products" onClick={() => setOpen(false)}>
                        Browse flowers
                      </Link>
                    </Button>
                  </>
                ) : (
                  <Button asChild size="lg" className="mt-4 h-12 w-full text-base">
                    <Link href="/checkout" onClick={() => setOpen(false)}>
                      Proceed to checkout
                    </Link>
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>

    {/* Drawer: play video when opened (large view) */}
    <Dialog open={!!previewVideo} onOpenChange={(o) => !o && setPreviewVideo(null)}>
      <DialogContent className="max-w-[min(90vw,640px)] p-0 overflow-hidden">
        <DialogHeader className="sr-only">
          <DialogTitle>{previewVideo?.title ?? "Video"}</DialogTitle>
          <DialogDescription className="sr-only">Product video preview</DialogDescription>
        </DialogHeader>
        {previewVideo && (
          <CartPreviewVideo
            key={previewVideo.imageKey}
            src={mediaUrl(previewVideo.imageKey, previewVideo.imageUrlRaw)}
            title={previewVideo.title}
          />
        )}
      </DialogContent>
    </Dialog>
    </>
  );
}
